import { callFilterRequest, resetFilterRequest } from '../api/filter';
import {
  COLLECTION_SELECTED,
  NEXT_PAGE,
  PREV_PAGE,
  SELECTED_PRODUCT_ID,
  SELECTED_PRODUCT_PAGE,
} from '../constants/pagination';
import { getFilterSettings } from '../filter-tree';
import { updateProductCount } from '../product-count';
import {
  calcPageAction,
  debounce,
  deleteQueryParamHistory,
  getSessionStorage,
  getThemeSettings,
  getUrlScheme,
  isMobile,
  scrollToTop,
  setQueryParamHistory,
  setSessionStorage,
} from '../utils';

let observer;

/**
 * @type {import('./index.d').pagination}
 */
export const pagination = (context, target) => {
  const [getPage] = context.useContextState('pagination ', {
    page: 1,
  });

  let behavior = 'refresh';

  let newPage = getPage().page || 1;

  switch (true) {
    // page link
    case target.classList.contains('boost-sd__pagination-number'): {
      const pageValue = target.textContent.trim();
      newPage = calcPageAction(newPage, pageValue);
      break;
    }

    case target.classList.contains('boost-sd__pagination-button--prev'): {
      newPage = calcPageAction(newPage, 'prev');
      break;
    }

    case target.classList.contains('boost-sd__pagination-button--next'): {
      newPage = calcPageAction(newPage, 'next');
      break;
    }

    // load more/infinite scroll
    case target.closest('.boost-sd__pagination-button--load-previous') !== null:
    case target.closest('.boost-sd__pagination-infinite-scroll-container-button') !== null:
      newPage = calcPageAction(getSessionStorage(PREV_PAGE), 'prev');
      behavior = 'previous';
      break;
    case target.closest('.boost-sd__pagination-button--load-more') !== null:
      newPage = calcPageAction(getSessionStorage(NEXT_PAGE), 'next');
      behavior = 'more';
      break;
  }

  processPaginatingAction(context, newPage, behavior);
};

/**
 * @type {import('./index.d').processPaginatingAction}
 */
const processPaginatingAction = (context, newPage, behavior) => {
  const { paginationTypeAdvanced = true } = context?.app?.generalSettings || {};

  const [getPage, setPage] = context.useContextState('pagination ', {
    page: 1,
  });

  const { paginationType = 'default' } =
    getThemeSettings(context)?.additionalElements?.pagination || {};

  const widgetId =
    paginationType === 'default' ? context.widgetId : context.app.templateMetadata.productList;

  behavior = paginationType !== 'default' ? behavior : 'refresh';

  if (behavior === 'more') {
    setSessionStorage(NEXT_PAGE, newPage);
  }
  if (behavior === 'previous') {
    setSessionStorage(PREV_PAGE, newPage);
  }

  const { isShortenUrlParam } = getFilterSettings(context);
  if (newPage !== Number(getPage().page)) {
    setPage({ page: newPage });
    if (newPage === 1) {
      deleteQueryParamHistory('page', {
        preventPushHistory: false,
        isShortenUrlParam,
        urlScheme: getUrlScheme(),
      });
    } else {
      paginationTypeAdvanced &&
        setQueryParamHistory('page', newPage, false, {
          isShortenUrlParam,
          urlScheme: getUrlScheme(),
        });
    }

    callFilterRequest(context, {
      additionParams: {
        widgetId,
        behavior,
      },
    });

    if (paginationType === 'default') {
      scrollToTop();
    } else {
      handlePaginationLoadMoreButtons(context, newPage);
    }
  }
};

/**
 * @type {import('./index.d').handlePaginationInfiniteScroll}
 */
export const handlePaginationInfiniteScroll = (context) => {
  const [getIsLoading, setIsLoading] = context.useContextState(
    'pagination-infinite-loading ',
    false
  );

  const target = context.document.querySelector(
    '.boost-sd__pagination-infinite-scroll-container-target'
  );

  let totalPage = 0;

  const productList = context.document.querySelector('.boost-sd__product-list');

  if (productList) {
    totalPage = Number(productList.getAttribute('data-total-page'));
  }

  const handleIntersect = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        if (getIsLoading()) return;

        setIsLoading(true);

        if (getSessionStorage(NEXT_PAGE) < totalPage) {
          debounce(processPaginatingAction(context, getSessionStorage(NEXT_PAGE) + 1, 'more'), 350);
        }
      }
    });
  };

  if (target) {
    const options = {
      rootMargin: '0px',
      threshold: 0.75,
    };

    observer = new IntersectionObserver(handleIntersect, options);

    observer.observe(target);
  }
};

/**
 * @type {import('./index.d').handlePaginationLoadMoreButtons}
 */
const handlePaginationLoadMoreButtons = (context, currentPage) => {
  const paginationLoadPreviousButton = context.document.querySelector(
    '.boost-sd__pagination-load-more-container-item[data-position="top"], .boost-sd__pagination-infinite-scroll-container-button'
  );

  const productCountPrev = context.document.querySelector(
    '.boost-sd__product-count[data-position="top"]'
  );

  const paginationLoadMoreButton = context.document.querySelector(
    '.boost-sd__pagination-load-more-container-item[data-position="bottom"]'
  );

  const productCountNext = context.document.querySelector(
    '.boost-sd__product-count[data-position="bottom"]'
  );

  const limit = context?.defaultParams?.limit || 24;

  let totalPage = 0;
  let totalProduct = 0;
  const productList = context.document.querySelector('.boost-sd__product-list');

  if (productList) {
    totalPage = Number(productList.getAttribute('data-total-page'));
    totalProduct = Number(productList.getAttribute('data-total-product'));
  }

  if (currentPage <= 1) {
    if (paginationLoadPreviousButton) paginationLoadPreviousButton.style.display = 'none';
    if (productCountPrev) productCountPrev.style.display = 'none';
  }
  if (currentPage === totalPage) {
    if (paginationLoadMoreButton) paginationLoadMoreButton.style.display = 'none';
    if (productCountNext) productCountNext.style.display = 'none';
  }

  updateProductCount(context, limit, totalProduct);
};

const scrollToProduct = (product) => {
  const top =
    window.boostWidgetIntegrationConfig?.scrollBackTopPosition ||
    product.getBoundingClientRect().top + window.scrollY;

  window.scroll({
    top,
    behavior: 'smooth',
  });
};

/**
 * Gets the current collection identifier from the URL pathname
 * @type {import('./index.d').getCurrentCollection}
 * @returns {string} The current collection identifier
 */
export const getCurrentCollection = () => {
  // Get URL path safely
  const pathname = window.location.pathname;

  // Check for collection paths
  if (pathname.includes('/collections/')) {
    // Extract collection handle: /collections/summer-sale -> summer-sale
    const collectionRegex = /\/collections\/([^/?#]+)/;
    const match = pathname.match(collectionRegex);

    if (match && match[1]) {
      // For standard collection pages, return the collection handle
      return match[1];
    }
  }

  // Check for search pages with collection filter
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('collection_id')) {
    return urlParams.get('collection_id');
  }

  // For other pages, use full pathname as context identifier
  return pathname;
};

/**
 * @type {import('./index.d').scrollRestoration}
 */
export const scrollRestoration = (context) => {
  if (window.history.scrollRestoration) {
    window.history.scrollRestoration = 'manual';
  }

  const listener = (event) => {
    // Webpage is loading from cache
    if (event.persisted) {
      scrollRestoration(context);
    }
  };

  const productList = context.$('.boost-sd__product-list');

  if (productList) {
    const productId = getSessionStorage(SELECTED_PRODUCT_ID);
    const storedCollection = getSessionStorage(COLLECTION_SELECTED);
    const currentCollection = getCurrentCollection();

    // Only restore scroll if product ID exists and we're in the same collection
    if (productId && storedCollection === currentCollection) {
      const product = document.querySelector(
        `.boost-sd__product-item[data-product-id="${productId}"]`
      );

      // Has product id and not in recommendation
      if (product && !product.closest('.boost-sd__recommendation')) {
        const equalHeightWrapper = product.querySelector('.boost-sd__product-image-wrapper--auto');
        let attempts = 0;
        const MAX_ATTEMPTS = 20;

        if (equalHeightWrapper) {
          // Only wait for aspect ratio if using auto ratio
          const intervalId = setInterval(() => {
            if (equalHeightWrapper.offsetHeight > 0 || attempts++ > MAX_ATTEMPTS) {
              scrollToProduct(product);
              clearInterval(intervalId);
            }
          }, 50);
        } else {
          // Immediate scroll for fixed ratio
          scrollToProduct(product);
        }
      }
    }

    // Clear stored values after restoration attempt or when collections don't match
    if (productId) {
      setSessionStorage(SELECTED_PRODUCT_ID, null);
      setSessionStorage(SELECTED_PRODUCT_PAGE, null);
      setSessionStorage(COLLECTION_SELECTED, null);
    }
  }

  // Handle IOS Scroll Restoration when page is loaded from cache
  window.addEventListener('pageshow', listener);

  return () => {
    window.removeEventListener('pageshow', listener);
  };
};

/**
 * @type {import('./index.d').handleProductListResponsive}
 */
export const handleProductListResponsive = (context) => {
  const productList = context.$('.boost-sd__product-list');
  const productListListColumn = context.$('.boost-sd__product-list-list-col');

  if (!productList) {
    return;
  }

  window.addEventListener('resize', function () {
    const viewAsIconGridActive = context.$('.boost-sd__view-as-icon--active');
    const viewAsIconGrid = context.$('.boost-sd__view-as-icon--grid');

    const { productsPerRowOnDesktop, productsPerRowOnMobile } = context.app.templateSettings
      ?.themeSettings?.productList || {
      productsPerRowOnMobile: 2,
      productsPerRowOnDesktop: 3,
    };
    const { listType } = context.app.templateSettings?.themeSettings?.additionalElements?.toolbar
      ?.elements?.viewAs || { listType: 'grid/list' };
    const isMobileSize = isMobile(context.app.generalSettings.breakpointtabletportraitmax || 991);
    const gridValue = viewAsIconGridActive?.getAttribute('data-value');

    const productListGridDesktop = 'boost-sd__product-list-' + gridValue + '-col';
    const productListGridMobile = 'boost-sd__product-list-grid--' + productsPerRowOnMobile + '-col';

    if (isMobileSize && !productListListColumn) {
      productList.classList.remove(productListGridDesktop);
      productList.classList.add(productListGridMobile);
      viewAsIconGrid?.setAttribute('data-value', 'grid--' + productsPerRowOnMobile);
    }

    if (!isMobileSize && !productListListColumn) {
      productList.classList.remove(productListGridMobile);
      productList.classList.add(productListGridDesktop);
      if (listType === 'grid/list') {
        viewAsIconGrid?.setAttribute('data-value', 'grid--' + productsPerRowOnDesktop);
      } else {
        viewAsIconGrid?.setAttribute('data-value', gridValue);
      }
    }
  });
};

/**
 * @type {import('./index.d').handleExceededPage}
 */
export const handleExceededPage = async (context) => {
  // reset filter request if page exceeded total page
  const productList = context.document.querySelector('.boost-sd__product-list');

  if (productList) {
    const totalPage = Number(productList.getAttribute('data-total-page'));
    const pageParam = Number(context.defaultParams.page) || 1;

    if (pageParam > totalPage) {
      return resetFilterRequest(context);
    }
  }
};
